import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import "./book-upload-modal.css";

interface CheckInResult {
  inserted: number;
  updated?: Array<{ row: number; reason: string }>;
  skipped?: Array<{ row: number; reason: string }>;
  failed: Array<{ row: number; errors: string[] }>;
}
interface CheckOutResult {
  collected: number;
  failed: Array<{ row: number; errors: string[] }>;
}
type UploadResult = CheckInResult | CheckOutResult;

export type UploadMode = "checkin" | "checkout";

const MODE_CONFIG: Record<UploadMode, {
  endpoint: string;
  successKey: "inserted" | "collected";
}> = {
  checkin: { endpoint: "/api/admin/books/upload", successKey: "inserted" },
  checkout: { endpoint: "/api/admin/checkout/upload", successKey: "collected" },
};

/**
 * Modal for bulk-importing books from an Excel/CSV/.gsheet file. Reused on
 * admin Entrada (mode="checkin"), Catálogo (mode="checkin") and Saída
 * (mode="checkout") so Grace can upload from wherever she happens to be.
 */
export function BookUploadModal({
  onClose,
  mode = "checkin",
}: {
  onClose: () => void;
  mode?: UploadMode;
}) {
  const { t } = useTranslation();
  const cfg = MODE_CONFIG[mode];
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const title = mode === "checkin" ? t("admin.upload.titleCheckin") : t("admin.upload.titleCheckout");
  const successWord = (n: number) =>
    mode === "checkin"
      ? t("admin.upload.wordImportedBook", { count: n })
      : t("admin.upload.wordRecordedCheckout", { count: n });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(cfg.endpoint, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      const data = (await res.json()) as UploadResult | { error: string };
      if (!res.ok) {
        setError("error" in data ? data.error : `HTTP ${res.status}`);
        return;
      }
      setResult(data as UploadResult);
      const successCount = (data as unknown as Record<string, unknown>)[cfg.successKey] as number;
      if (successCount > 0) {
        // Refresh anything that might be displaying book counts or lists.
        qc.invalidateQueries({ queryKey: ["admin", "books-list"] });
        qc.invalidateQueries({ queryKey: ["admin", "stats"] });
        qc.invalidateQueries({ queryKey: ["books"] });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="upload-title">
      <div className="modal">
        <h3 id="upload-title">{title}</h3>
        {!result ? (
          <form onSubmit={onSubmit} className="modal__form">
            <div className="field">
              <label htmlFor="upload-file">{t("admin.upload.fileLabel")}</label>
              <input
                id="upload-file"
                type="file"
                accept=".xlsx,.csv,.ods,application/vnd.oasis.opendocument.spreadsheet,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                required
                disabled={submitting}
              />
              <p className="field__help">{t("admin.upload.fileHelp")}</p>
            </div>
            <details className="book-upload__help">
              <summary>{t("admin.upload.columnsSummary")}</summary>
              <p>{t("admin.upload.columnsIntro")}</p>
              {mode === "checkin" ? (
                <ul>
                  <li>{t("admin.upload.ciTitle")}</li>
                  <li>{t("admin.upload.ciAuthor")}</li>
                  <li>{t("admin.upload.ciIsbn")}</li>
                  <li>{t("admin.upload.ciGenre")}</li>
                  <li>{t("admin.upload.ciCondition")}</li>
                  <li>{t("admin.upload.ciDonor")}</li>
                  <li>{t("admin.upload.ciNotes")}</li>
                  <li>{t("admin.upload.ciNoCredits")}</li>
                </ul>
              ) : (
                <ul>
                  <li>{t("admin.upload.coIsbn")}</li>
                  <li>{t("admin.upload.coTitle")}</li>
                  <li>{t("admin.upload.coUser")}</li>
                  <li>{t("admin.upload.coNoCredits")}</li>
                </ul>
              )}
              <p>
                {mode === "checkin" ? t("admin.upload.noteCheckin") : t("admin.upload.noteCheckout")}{" "}
                {t("admin.upload.noteCommon")}
              </p>
            </details>
            {error && (
              <p className="field__error" role="alert">{error}</p>
            )}
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                {t("common.cancel")}
              </button>
              <button type="submit" className="btn btn-primary" disabled={!file || submitting}>
                {submitting ? t("admin.upload.processing") : t("admin.upload.import")}
              </button>
            </div>
          </form>
        ) : (
          <div className="book-upload__result">
            <p>
              <strong>{(result as unknown as Record<string, number>)[cfg.successKey]}</strong>{" "}
              {successWord((result as unknown as Record<string, number>)[cfg.successKey] ?? 0)}.
              {"updated" in result && result.updated && result.updated.length > 0 && (
                <>
                  {" "}
                  <strong>{result.updated.length}</strong>{" "}
                  {t("admin.upload.rowsUpdated", { count: result.updated.length })}{" "}
                  {t("admin.upload.rowUpdatedSuffix")}
                </>
              )}
              {"skipped" in result && result.skipped && result.skipped.length > 0 && (
                <>
                  {" "}
                  <strong>{result.skipped.length}</strong>{" "}
                  {t("admin.upload.rowsSkipped", { count: result.skipped.length })}.
                </>
              )}
              {result.failed.length > 0 && (
                <>
                  {" "}
                  <strong className="book-upload__fail">{result.failed.length}</strong>{" "}
                  {t("admin.upload.rowsFailed", { count: result.failed.length })}.
                </>
              )}
            </p>
            {"updated" in result && result.updated && result.updated.length > 0 && (
              <details className="book-upload__skipped-details">
                <summary>{t("admin.upload.updatedDetails", { count: result.updated.length })}</summary>
                <ul className="book-upload__skipped">
                  {result.updated.slice(0, 50).map((u) => (
                    <li key={u.row}>
                      <strong>{t("admin.upload.rowLabel", { row: u.row })}</strong> {u.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {"skipped" in result && result.skipped && result.skipped.length > 0 && (
              <details className="book-upload__skipped-details">
                <summary>{t("admin.upload.skippedDetails", { count: result.skipped.length })}</summary>
                <ul className="book-upload__skipped">
                  {result.skipped.slice(0, 50).map((s) => (
                    <li key={s.row}>
                      <strong>{t("admin.upload.rowLabel", { row: s.row })}</strong> {s.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {result.failed.length > 0 && (
              <ul className="book-upload__errors">
                {result.failed.slice(0, 25).map((f) => (
                  <li key={f.row}>
                    <strong>{t("admin.upload.rowLabel", { row: f.row })}</strong> {f.errors.join("; ")}
                  </li>
                ))}
                {result.failed.length > 25 && (
                  <li>{t("admin.upload.andMore", { count: result.failed.length - 25 })}</li>
                )}
              </ul>
            )}
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setResult(null);
                  setFile(null);
                }}
              >
                {t("admin.upload.importAnother")}
              </button>
              <button type="button" className="btn btn-primary" onClick={onClose}>
                {t("admin.upload.close")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
